import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  UserPlus, Search, Stethoscope, Users, Calendar,
  Loader2, Trash2, Edit, AlertCircle, CheckCircle,
  CreditCard, RefreshCw, Mail
} from "lucide-react";
import { toast } from "sonner";

const AdminPrivatePhysicians = () => {
  const { token } = useAuth();
  const [physicians, setPhysicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(null);
  
  const [assignForm, setAssignForm] = useState({
    physician_email: "",
    license_count: 10,
    expiration_date: "",
    notes: ""
  });
  
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    organization_name: "",
    specialty: "",
    license_count: 10,
    expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    notes: ""
  });
  
  const [editForm, setEditForm] = useState({
    license_count: 0,
    expiration_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchPrivatePhysicians();
  }, [token]);

  const fetchPrivatePhysicians = async () => {
    try {
      const response = await fetch(`${API}/admin/private-physicians`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhysicians(data.private_physicians || []);
      } else {
        toast.error("Failed to load private physicians");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load private physicians");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignLicense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/admin/private-physicians/assign`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        
        body: JSON.stringify(assignForm)
      });
      
      if (response.ok) {
        toast.success("License assigned successfully!");
        setShowAssignModal(false);
        setAssignForm({ physician_email: "", license_count: 10, expiration_date: "", notes: "" });
        fetchPrivatePhysicians();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to assign license");
      }
    } catch (error) {
      console.error("Assign error:", error);
      toast.error("Failed to assign license");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePhysician = async (e) => {
    e.preventDefault();
    
    if (!createForm.name || !createForm.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/admin/private-physicians/create`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(createForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        const creds = data.credentials;
        if (data.email_sent) {
          toast.success(`Physician created! Welcome email sent to ${creds.email}`);
        } else {
          toast.success(`Physician created! Email not sent — share credentials manually: ${creds.email} / ${creds.temporary_password}`);
        }
        setShowCreateModal(false);
        setCreateForm({
          name: "",
          email: "",
          organization_name: "",
          specialty: "",
          license_count: 10,
          expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          notes: ""
        });
        fetchPrivatePhysicians();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create physician");
      }
    } catch (error) {
      console.error("Create error:", error);
      toast.error("Failed to create physician");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLicense = async (e) => {
    e.preventDefault();
    if (!selectedPhysician) return;
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/admin/private-physicians/${selectedPhysician.physician_id}`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success("License updated successfully!");
        setShowEditModal(false);
        setSelectedPhysician(null);
        fetchPrivatePhysicians();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update license");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update license");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeLicense = async (physicianId, physicianName) => {
    if (!confirm(`Are you sure you want to revoke the license for ${physicianName}?`)) return;
    
    try {
      const response = await fetch(`${API}/admin/private-physicians/${physicianId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        toast.success("License revoked");
        fetchPrivatePhysicians();
      } else {
        toast.error("Failed to revoke license");
      }
    } catch (error) {
      toast.error("Failed to revoke license");
    }
  };

  const openEditModal = (physician) => {
    setSelectedPhysician(physician);
    setEditForm({
      license_count: physician.license_count,
      expiration_date: physician.expiration_date?.split("T")[0] || "",
      notes: physician.notes || ""
    });
    setShowEditModal(true);
  };

  const filteredPhysicians = physicians.filter(
    (p) =>
      p.physician_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.physician_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Set default expiration date to 1 year from now
  const getDefaultExpiration = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split("T")[0];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-secondary">Private Physicians</h2>
          <p className="text-muted-foreground">
            Manage licenses for independent practice physicians
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="rounded-full bg-green-600 hover:bg-green-700"
            data-testid="create-physician-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create New Physician
          </Button>
          <Button 
            onClick={() => {
              setAssignForm({ 
                ...assignForm, 
                expiration_date: getDefaultExpiration() 
              });
              setShowAssignModal(true);
            }}
            className="rounded-full bg-primary"
            data-testid="assign-license-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Assign License
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="p-4 bg-white border border-orange-100">
          <p className="text-sm text-muted-foreground">Total Private Physicians</p>
          <p className="text-2xl font-bold text-secondary">{physicians.length}</p>
        </Card>
        <Card className="p-4 bg-white border border-orange-100">
          <p className="text-sm text-muted-foreground">Total Licenses Assigned</p>
          <p className="text-2xl font-bold text-primary">
            {physicians.reduce((sum, p) => sum + (p.license_count || 0), 0)}
          </p>
        </Card>
        <Card className="p-4 bg-white border border-orange-100">
          <p className="text-sm text-muted-foreground">Active Patients</p>
          <p className="text-2xl font-bold text-green-600">
            {physicians.reduce((sum, p) => sum + (p.used_licenses || 0), 0)}
          </p>
        </Card>
        <Card className="p-4 bg-white border border-orange-100">
          <p className="text-sm text-muted-foreground">Expiring Soon</p>
          <p className="text-2xl font-bold text-yellow-600">
            {physicians.filter(p => {
              const exp = new Date(p.expiration_date);
              const thirtyDays = new Date();
              thirtyDays.setDate(thirtyDays.getDate() + 30);
              return exp < thirtyDays && exp > new Date();
            }).length}
          </p>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-full border-orange-200"
        />
      </div>

      {/* Table */}
      <Card className="rounded-2xl border-orange-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-muted-foreground">Loading...</p>
          </div>
        ) : physicians.length === 0 ? (
          <div className="p-8 text-center">
            <Stethoscope className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-semibold text-secondary">No Private Physicians</h3>
            <p className="text-muted-foreground text-sm mt-1">
              Assign licenses to independent physicians who don't belong to an organization.
            </p>
            <Button 
              onClick={() => setShowAssignModal(true)}
              className="mt-4 rounded-full"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Assign First License
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-50/50">
                <tr>
                  <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm">Physician</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm hidden sm:table-cell">Licenses</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm hidden md:table-cell">Usage</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm hidden md:table-cell">Expiration</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm">Status</th>
                  <th className="text-right p-3 sm:p-4 font-medium text-secondary text-xs sm:text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPhysicians.map((physician) => {
                  const expired = isExpired(physician.expiration_date);
                  const usagePercent = physician.license_count > 0 
                    ? Math.round((physician.used_licenses / physician.license_count) * 100) 
                    : 0;
                  
                  return (
                    <tr key={physician.physician_id} className="border-t border-orange-100 hover:bg-blue-50/30">
                      <td className="p-3 sm:p-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                            <Stethoscope className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-secondary text-sm truncate">{physician.physician_name || "Unknown"}</p>
                            <p className="text-xs text-muted-foreground truncate">{physician.physician_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 hidden sm:table-cell">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-muted-foreground" />
                          <span className="font-bold text-secondary">{physician.license_count}</span>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 hidden md:table-cell">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span>{physician.used_licenses} used</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-green-600">{physician.remaining_licenses} avail</span>
                          </div>
                          <div className="w-24 sm:w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${usagePercent > 80 ? 'bg-red-500' : usagePercent > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${usagePercent}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className={`text-xs sm:text-sm ${expired ? "text-red-500" : ""}`}>
                            {physician.expiration_date?.split("T")[0] || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4">
                        {expired ? (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Expired
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 sm:p-4">
                        <div className="flex justify-end gap-1 sm:gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(physician)}
                            className="text-blue-600 hover:bg-blue-50"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeLicense(physician.physician_id, physician.physician_name)}
                            className="text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Assign License Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Private License</DialogTitle>
            <DialogDescription>
              Assign licenses directly to an independent physician who is not part of any organization.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAssignLicense} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Physician Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="doctor@example.com"
                value={assignForm.physician_email}
                onChange={(e) => setAssignForm({ ...assignForm, physician_email: e.target.value })}
                required
                className="rounded-xl"
              />
              <p className="text-xs text-muted-foreground">
                The physician must have registered and be marked as a physician/dietitian role.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="licenses">Number of Licenses *</Label>
              <Input
                id="licenses"
                type="number"
                min="1"
                max="1000"
                value={assignForm.license_count}
                onChange={(e) => setAssignForm({ ...assignForm, license_count: parseInt(e.target.value) || 1 })}
                required
                className="rounded-xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiration">Expiration Date *</Label>
              <Input
                id="expiration"
                type="date"
                value={assignForm.expiration_date}
                onChange={(e) => setAssignForm({ ...assignForm, expiration_date: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                placeholder="e.g., Annual subscription, Trial license..."
                value={assignForm.notes}
                onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                className="rounded-xl"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-primary">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Assign License
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit License Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update License</DialogTitle>
            <DialogDescription>
              Update license for {selectedPhysician?.physician_name || selectedPhysician?.physician_email}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateLicense} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-licenses">Number of Licenses</Label>
              <Input
                id="edit-licenses"
                type="number"
                min="1"
                max="1000"
                value={editForm.license_count}
                onChange={(e) => setEditForm({ ...editForm, license_count: parseInt(e.target.value) || 1 })}
                className="rounded-xl"
              />
              <p className="text-xs text-muted-foreground">
                Currently using {selectedPhysician?.used_licenses || 0} licenses.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-expiration">Expiration Date</Label>
              <Input
                id="edit-expiration"
                type="date"
                value={editForm.expiration_date}
                onChange={(e) => setEditForm({ ...editForm, expiration_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                placeholder="e.g., Renewed for another year..."
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="rounded-xl"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-primary">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Update License
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create New Physician Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Private Physician</DialogTitle>
            <DialogDescription>
              Create a new physician account with login credentials and assign licenses.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreatePhysician} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">Full Name *</Label>
                <Input
                  id="create-name"
                  placeholder="Dr. John Smith"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-email">Email *</Label>
                <Input
                  id="create-email"
                  type="email"
                  placeholder="doctor@clinic.com"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
              <p className="text-sm text-blue-700 font-medium">Auto-generated password</p>
              <p className="text-xs text-blue-600 mt-1">
                A secure temporary password will be generated and emailed to the physician. They must change it on first login.
              </p>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-secondary mb-3">Organization Details (for credibility)</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-org">Organization/Clinic Name</Label>
                  <Input
                    id="create-org"
                    placeholder="City Medical Center"
                    value={createForm.organization_name}
                    onChange={(e) => setCreateForm({ ...createForm, organization_name: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="create-specialty">Specialty</Label>
                  <Input
                    id="create-specialty"
                    placeholder="e.g., Endocrinologist"
                    value={createForm.specialty}
                    onChange={(e) => setCreateForm({ ...createForm, specialty: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-secondary mb-3">License Settings</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-licenses">Number of Licenses *</Label>
                  <Input
                    id="create-licenses"
                    type="number"
                    min="1"
                    max="1000"
                    value={createForm.license_count}
                    onChange={(e) => setCreateForm({ ...createForm, license_count: parseInt(e.target.value) || 1 })}
                    required
                    className="rounded-xl"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="create-expiration">Expiration Date *</Label>
                  <Input
                    id="create-expiration"
                    type="date"
                    value={createForm.expiration_date}
                    onChange={(e) => setCreateForm({ ...createForm, expiration_date: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-notes">Notes</Label>
              <Input
                id="create-notes"
                placeholder="Any additional notes..."
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                className="rounded-xl"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Create Physician
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPrivatePhysicians;
